import { Link } from "react-router-dom";
import {
    Phone, ArrowUpRight, Clock, MapPin, ShieldCheck, KeyRound, DoorOpen,
    Wrench, Lock, ChevronRight, Sparkles, BadgeCheck, Hammer
} from "lucide-react";
import { Overline, SectionHeader, Stat } from "@/components/Section";
import CTABand from "@/components/CTABand";
import HeroArtisanImage from "@/components/HeroArtisanImage";
import TrustBadges from "@/components/TrustBadges";
import { useCity } from "@/context/LocationContext";

const SERVICES = [
    {
        icon: DoorOpen,
        title: "Ouverture de porte",
        desc: "Porte claquée ou bloquée, ouverture sans dégât en quelques minutes par notre artisan.",
        to: "/ouverture-de-porte",
    },
    {
        icon: Wrench,
        title: "Dépannage serrurier",
        desc: "Clé cassée, mécanisme grippé, cylindre HS — diagnostic et réparation 24/7.",
        to: "/depannage-serrurier",
    },
    {
        icon: Lock,
        title: "Remplacement de serrure",
        desc: "Pose de serrures haute sécurité certifiées A2P, cylindres anti-effraction.",
        to: "/remplacement-serrure",
    },
    {
        icon: ShieldCheck,
        title: "Pose de porte blindée",
        desc: "Installation de portes blindées et blocs-portes anti-effraction sur mesure.",
        to: "/remplacement-serrure",
    },
];

const STEPS = [
    { n: "01", t: "Vous appelez", d: "Un artisan vous répond directement, 24h/24, et établit un diagnostic au téléphone." },
    { n: "02", t: "Devis transparent", d: "Tarif annoncé avant l'intervention. Aucune surprise, aucun frais caché." },
    { n: "03", t: "Intervention rapide", d: "Déplacement sous 30 min en moyenne dans votre commune avec véhicule équipé." },
    { n: "04", t: "Travail garanti", d: "Pièces certifiées, facture claire, garantie sur la prestation et le matériel." },
];

const Home = () => {
    const { city, postal } = useCity();
    const zoneLong = city ? `${city}${postal ? " " + postal : ""} · Dépannage Express` : "Dépannage Express";
    return (
        <>
            {/* HERO */}
            <section className="relative border-b border-black/10 grid-bg" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7 fade-up fade-up-1">
                            <Overline className="mb-5">◉ {zoneLong} · Intervention 24/7</Overline>
                            <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.95]">
                                Artisan
                                <span className="block">
                                    serrurier{city ? <span className="text-[#E63946]"> {city}{postal ? " " + postal : ""}</span> : ""}
                                </span>
                                <span className="inline-flex items-center gap-3">
                                    <span className="text-[#E63946]">disponible</span>
                                </span>
                                <span className="block">dans la commune.</span>
                            </h1>
                            <p className="mt-7 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
                                <span className="font-semibold text-[#0A0A0A]">Atelier Beauchamp</span> intervient en urgence pour
                                l'ouverture de porte claquée ou bloquée, le remplacement de serrure, la réparation et la pose de porte blindée.
                                Tarif annoncé avant intervention, déplacement rapide, travail soigné.
                            </p>

                            <div className="mt-9 flex flex-col sm:flex-row gap-3">
                                <a
                                    href="tel:+33649501843"
                                    data-testid="hero-call-btn"
                                    className="inline-flex items-center justify-between gap-4 bg-[#E63946] hover:bg-[#D62828] text-white px-6 py-4 transition-colors duration-200"
                                >
                                    <span className="flex items-center gap-3">
                                        <Phone className="h-5 w-5" strokeWidth={2.4} />
                                        <span className="font-display font-bold text-lg tracking-tight">06 49 50 18 43</span>
                                    </span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </a>
                                <Link
                                    to="/contact"
                                    data-testid="hero-quote-btn"
                                    className="inline-flex items-center justify-between gap-4 bg-[#0A0A0A] hover:bg-zinc-800 text-white px-6 py-4 transition-colors duration-200"
                                >
                                    <span className="font-semibold text-sm uppercase tracking-[0.15em]">Devis gratuit</span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </Link>
                            </div>

                            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-5">
                                <Stat value="24/7" label="Disponibilité" />
                                <Stat value="~30min" label="Temps moyen" />
                                <Stat value="A2P" label="Serrures certifiées" />
                                <Stat value="100%" label="Devis gratuit" />
                            </div>
                        </div>

                        <div className="lg:col-span-5 fade-up fade-up-2">
                            <HeroArtisanImage />
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST STRIP — bandeau "Agréé · A2P · Artisan · Devis" juste sous la photo */}
            <section className="border-b border-black/10 bg-white" data-testid="trust-strip">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
                    {[
                        { icon: ShieldCheck, label: "Agréé & diplômé" },
                        { icon: BadgeCheck, label: "Serrures A2P certifiées" },
                        { icon: Hammer, label: "Artisan indépendant" },
                        { icon: Sparkles, label: "Devis gratuit & transparent" },
                    ].map((it) => (
                        <div key={it.label} className="flex items-center gap-3 text-sm text-zinc-700">
                            <it.icon className="h-5 w-5 text-[#0A0A0A]" strokeWidth={2.2} />
                            <span className="font-medium">{it.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* CERTIFICATIONS & ENGAGEMENTS — juste après la première photo (hero) */}
            <TrustBadges />

            {/* SERVICES */}
            <section className="py-20 lg:py-28" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeader
                        kicker="◉ Nos prestations"
                        title="Toute la serrurerie, par un seul artisan."
                        lead="De l'urgence nocturne à la pose de porte blindée, Atelier Beauchamp couvre l'ensemble des prestations de serrurerie pour les particuliers et les professionnels — dans la commune."
                    />

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {SERVICES.map((s) => (
                            <Link
                                key={s.title}
                                to={s.to}
                                data-testid={`service-card-${s.title}`}
                                className="group bg-white border border-black/10 p-7 sm:p-9 hover:-translate-y-1 hover:border-black transition-all duration-200 flex flex-col"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="h-12 w-12 grid place-items-center bg-[#0A0A0A] text-white group-hover:bg-[#E63946] transition-colors">
                                        <s.icon className="h-5 w-5" strokeWidth={2.2} />
                                    </div>
                                    <ArrowUpRight className="h-5 w-5 text-zinc-400 group-hover:text-[#0A0A0A] transition-colors" />
                                </div>
                                <h3 className="mt-7 font-display font-bold text-xl sm:text-2xl uppercase tracking-tight">{s.title}</h3>
                                <p className="mt-3 text-sm sm:text-base text-zinc-600 leading-relaxed">{s.desc}</p>
                                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#0A0A0A]">
                                    En savoir plus <ChevronRight className="h-3.5 w-3.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS */}
            <section className="bg-white border-y border-black/10 py-20 lg:py-28" data-testid="process-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <SectionHeader
                                kicker="◉ Méthode"
                                title="Une intervention claire, du premier appel à la garantie."
                                lead="Chez Atelier Beauchamp, pas de surprise. Vous savez exactement ce qui sera fait, à quel prix, et avec quelles garanties."
                            />
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10">
                            {STEPS.map((s) => (
                                <div key={s.n} className="bg-white p-7 sm:p-9 hover:bg-zinc-50 transition-colors">
                                    <div className="font-display text-5xl font-bold text-[#E63946] tracking-tight">{s.n}</div>
                                    <h4 className="mt-5 font-display font-bold text-lg uppercase tracking-tight">{s.t}</h4>
                                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{s.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ZONE D'INTERVENTION */}
            <section className="py-20 lg:py-28" data-testid="zone-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-5">
                            <Overline className="mb-4">◉ Couverture</Overline>
                            <h2 className="font-display font-bold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                                Plusieurs communes<br />de jour comme de nuit.
                            </h2>
                            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
                                Atelier Beauchamp se déplace dans la commune. Quelle que soit votre ville,
                                un artisan peut être chez vous en moins de 30 minutes en moyenne.
                            </p>
                            <div className="mt-7 flex items-center gap-3 text-sm">
                                <KeyRound className="h-4 w-4 text-[#E63946]" strokeWidth={2.4} />
                                <span className="text-zinc-700 font-medium">Devis gratuit avant toute intervention</span>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-black/10 border border-black/10">
                                {[
                                    "Intervention 24/7", "Devis gratuit", "Tarif annoncé",
                                    "Sans engagement", "Artisan local", "Travail garanti",
                                    "Toutes marques", "A2P certifiées", "Porte blindée",
                                    "Ouverture sans dégât", "Réparation rapide", "Mise en sécurité",
                                ].map((c) => (
                                    <div key={c} className="bg-white p-5 hover:bg-zinc-50 transition-colors flex items-center gap-2">
                                        <MapPin className="h-3.5 w-3.5 text-[#E63946]" strokeWidth={2.4} />
                                        <span className="text-sm font-medium">{c}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 text-xs text-zinc-500 uppercase tracking-[0.18em] font-semibold">
                                + intervention dans toute votre commune sur simple appel
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <CTABand />
        </>
    );
};

export default Home;
